import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { fetchCalendarAppointments } from "../../core/redux/slices/appointmentSlice";
import { fetchDoctors } from "../../core/redux/slices/doctorSlice";
import { fetchProcedures } from "../../core/redux/slices/procedureSlice";
import AppointmentModal from "../../core/modals/appointments/AppointmentModal";

const STATUS_COLORS = {
  Booked: "#3b82f6",
  Completed: "#22c55e",
  Cancelled: "#ef4444",
  NoShow: "#f59e0b",
};

const STATUS_BADGE = {
  Booked: "primary",
  Completed: "success",
  Cancelled: "danger",
  NoShow: "warning",
};

// Extract date string "YYYY-MM-DD" from API date (may be "2026-06-16T00:00:00")
const dateOnly = (v) => (v || "").split("T")[0];
// Formats a JS Date using its LOCAL calendar date (not toISOString's UTC conversion,
// which shifts the date backward a day in positive-UTC-offset timezones like IST —
// that's what made "Today"/single-day view miss today's appointments while wider
// Week/Month ranges still happened to include the shifted-but-still-in-range date).
const toLocalDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};
// Extract time "HH:mm:ss" from API time (may be "09:30:00.0000000")
const timeOnly = (v) => {
  if (!v) return "";
  const t = v.split(".")[0];
  return t.length === 5 ? `${t}:00` : t;
};

const fmt12 = (timeStr) => {
  if (!timeStr) return "";
  const [h, m] = timeStr.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
};

const TOOLTIP_OFFSET = { x: 14, y: 12 };

const AppointmentCalendar = () => {
  const dispatch = useDispatch();
  const calRef = useRef(null);
  const calendarEvents = useSelector((s) => s.appointments.calendarEvents);

  const [showModal, setShowModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [initialDate, setInitialDate] = useState("");
  const [initialTime, setInitialTime] = useState("");

  // Hover tooltip
  const [tooltip, setTooltip] = useState({ visible: false, data: null, x: 0, y: 0 });

  useEffect(() => {
    dispatch(fetchDoctors());
    dispatch(fetchProcedures());
  }, [dispatch]);

  const events = calendarEvents.map((a) => {
    const d = dateOnly(a.appointmentDate);
    const ft = timeOnly(a.fromTime);
    const tt = timeOnly(a.toTime);
    return {
      id: String(a.appointmentID || a.id || Math.random()),
      title: `${a.tokenNumber ? `#${a.tokenNumber} ` : ""}${a.patientName || ""}`,
      start: d && ft ? `${d}T${ft}` : d,
      end: d && tt ? `${d}T${tt}` : undefined,
      backgroundColor: STATUS_COLORS[a.status] || "#3b82f6",
      borderColor: STATUS_COLORS[a.status] || "#3b82f6",
      extendedProps: { ...a },
    };
  });

  const handleEventClick = ({ event }) => {
    setTooltip((t) => ({ ...t, visible: false }));
    setEditRecord(event.extendedProps);
    setInitialDate("");
    setInitialTime("");
    setShowModal(true);
  };

  const handleDateClick = ({ dateStr, date }) => {
    setEditRecord(null);
    setInitialDate(dateStr.split("T")[0]);
    const h = date.getHours();
    const m = date.getMinutes();
    setInitialTime(h ? `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}` : "");
    setShowModal(true);
  };

  const handleEventMouseEnter = ({ event, jsEvent }) => {
    setTooltip({
      visible: true,
      data: event.extendedProps,
      x: jsEvent.clientX,
      y: jsEvent.clientY,
    });
  };

  const handleEventMouseLeave = () => {
    setTooltip((t) => ({ ...t, visible: false }));
  };

  const handleMouseMove = (e) => {
    if (tooltip.visible) {
      setTooltip((t) => ({ ...t, x: e.clientX, y: e.clientY }));
    }
  };

  // Fetch for every day in the visible range
  const handleDatesSet = ({ start, end }) => {
    const from = toLocalDateStr(start);
    const to = toLocalDateStr(end);
    dispatch(fetchCalendarAppointments({ from, to }));
  };

  const closeModal = () => {
    setShowModal(false);
    setEditRecord(null);
    if (calRef.current) {
      try {
        const api = calRef.current.getApi();
        const from = toLocalDateStr(api.view.activeStart);
        const to = toLocalDateStr(api.view.activeEnd);
        dispatch(fetchCalendarAppointments({ from, to }));
      } catch (_e) { /* calendar may be unmounted */ }
    }
  };

  const tooltipData = tooltip.data;

  return (
    <div className="page-wrapper" onMouseMove={handleMouseMove}>
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>Appointment Calendar</h4>
            <h6>View and manage appointments</h6>
          </div>
          <div className="page-btn">
            <button
              className="btn btn-added"
              onClick={() => { setEditRecord(null); setInitialDate(""); setInitialTime(""); setShowModal(true); }}
            >
              + Book Appointment
            </button>
          </div>
        </div>

        <div className="card p-3">
          <div className="d-flex gap-3 mb-3 flex-wrap align-items-center">
            {Object.entries(STATUS_COLORS).map(([status, color]) => (
              <span key={status} className="d-flex align-items-center gap-1 small">
                <span style={{ display: "inline-block", width: 12, height: 12, borderRadius: 3, background: color }} />
                {status === "NoShow" ? "No Show" : status}
              </span>
            ))}
            <span className="ms-auto small text-muted">Click a slot to book · Click an event to view/update</span>
          </div>
          <FullCalendar
            ref={calRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="timeGridWeek"
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay",
            }}
            events={events}
            eventClick={handleEventClick}
            dateClick={handleDateClick}
            datesSet={handleDatesSet}
            eventMouseEnter={handleEventMouseEnter}
            eventMouseLeave={handleEventMouseLeave}
            slotDuration="00:10:00"
            slotLabelInterval="01:00:00"
            slotMinTime="07:00:00"
            slotMaxTime="21:00:00"
            allDaySlot={false}
            height="auto"
            eventTimeFormat={{ hour: "2-digit", minute: "2-digit", hour12: true }}
            snapDuration="00:10:00"
          />
        </div>
      </div>

      {/* Hover Tooltip */}
      {tooltip.visible && tooltipData && (
        <div
          style={{
            position: "fixed",
            top: tooltip.y + TOOLTIP_OFFSET.y,
            left: tooltip.x + TOOLTIP_OFFSET.x,
            zIndex: 99999,
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            padding: "12px 16px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            minWidth: 220,
            maxWidth: 280,
            pointerEvents: "none",
          }}
        >
          <div className="d-flex align-items-center gap-2 mb-1">
            {tooltipData.tokenNumber && (
              <span className="badge bg-secondary">#{tooltipData.tokenNumber}</span>
            )}
            <span
              className={`badge bg-${STATUS_BADGE[tooltipData.status] || "primary"}`}
            >
              {tooltipData.status === "NoShow" ? "No Show" : (tooltipData.status || "Booked")}
            </span>
          </div>
          <div className="fw-semibold" style={{ fontSize: 14 }}>{tooltipData.patientName || "—"}</div>
          {tooltipData.doctorName && (
            <div className="text-muted" style={{ fontSize: 12 }}>Dr. {tooltipData.doctorName}</div>
          )}
          {tooltipData.procedureName && (
            <div className="text-muted" style={{ fontSize: 12 }}>{tooltipData.procedureName}</div>
          )}
          <div className="mt-1 small">
            {fmt12(timeOnly(tooltipData.fromTime))}
            {tooltipData.toTime ? ` – ${fmt12(timeOnly(tooltipData.toTime))}` : ""}
          </div>
          {tooltipData.remarks && (
            <div className="mt-1 text-muted" style={{ fontSize: 11, borderTop: "1px solid #f0f0f0", paddingTop: 4 }}>
              {tooltipData.remarks}
            </div>
          )}
        </div>
      )}

      <AppointmentModal
        show={showModal}
        onClose={closeModal}
        editRecord={editRecord}
        initialDate={initialDate}
        initialTime={initialTime}
      />
    </div>
  );
};

export default AppointmentCalendar;
